import { Layout } from 'antd';
import { fetchApps, setCurrentApp } from 'app-redux/actions/apps.actions';
import { verifyRbacPermissions } from 'app-redux/actions/rbac.actions';
import { AppState } from 'app-redux/reducers';
import { getCurrentOrgId } from 'app-redux/selectors/orgs.selector';
import { getAllowedAndDeniedOperationsDTO, rbacPermissionsCheck } from 'app-redux/selectors/rbac.selector';
import { AppsPageHeader } from 'components/headers/apps-page-header';
import { AppsTable } from 'components/tables/apps-table';
import { IUserAccount } from 'core/accounts/models';
import { INavigationRouteProps } from 'core/navigation/models';
import { AllowedAndDeniedOperationsDTO, OperationListDTO } from 'core/rbac/models';
import { UUID } from 'core/utils/BasicModels';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { App, PagedApp } from '../../app-redux/reducers/apps.reducer';
import { currentUserHasPermission, getCurrentUser } from '../../app-redux/selectors/accounts.selector';
import { getAppList, getCurrentApp, getCurrentAppLoadingStatus } from '../../app-redux/selectors/apps.selector';
import { history, RoutePaths } from '../../core/history';
import './style.less';
import { useDebounced } from 'core/common-methods';
const {
  Content
} = Layout;
interface IGetAppProps extends INavigationRouteProps<unknown> {
  getAppsList: (orgId: UUID, pageNumber: number, pageSize: number) => Promise<PagedApp>;
  appList: App[];
  pages?: PagedApp;
  pageNumber: number;
  isLoadingApp: boolean;
  currentOrgId: string;
  currentUser?: IUserAccount;
  allowedAndDeniedOperationsDTO?: AllowedAndDeniedOperationsDTO;
  verifyRbacPermissions: (operationsListDTO: OperationListDTO) => Promise<void>;
  dispatchDeleteApp: (appId: UUID) => any;
  setCurrentApp: (appId: UUID) => any;
  rbacPermissionsCheck: (id: UUID, context: string, permission: string, scope: string, isDevConsoleCheck: boolean) => boolean;
  currentUserHasPermission: (id: UUID, context: string, permission: string, scope: string, isDevConsoleCheck: boolean) => boolean;
}

// tslint:disable-next-line:no-big-function
export const AppsComponent: React.FC<IGetAppProps> = ({
  currentUserHasPermission,
  verifyRbacPermissions,
  getAppsList,
  currentOrgId,
  ...props
}) => {
  const [apps, setApps] = useState(props.appList);
  const [search, setSearch] = useState<string>('');
  const [currentUserCanCreateApps, setCurrentUserCanCreateApps] = useState(false);
  const getOperationsToVerify = () => {
    return {
      operations: [{
        action: 'app:new',
        resource: 'crn:system'
      }]
    };
  };
  const verifyAccessToCreateApp = useCallback(async () => {
    await verifyRbacPermissions(getOperationsToVerify());
  }, [verifyRbacPermissions]);
  const checkUserHasPermissionDefaultValue = useCallback((id: UUID, context: string, permission: string, scope: string, isDevConsoleCheck = false) => {
    return currentUserHasPermission(id, context, permission, scope, isDevConsoleCheck);
  }, [currentUserHasPermission]);
  useEffect(() => {
    verifyAccessToCreateApp().catch();
  }, [verifyAccessToCreateApp]);
  useEffect(() => {
    const currUserCanCreateApps = checkUserHasPermissionDefaultValue(currentOrgId, 'ORGANIZATION', 'APPS', 'WRITE') || checkUserHasPermissionDefaultValue(currentOrgId, 'ORGANIZATION', 'OWNER', 'WRITE');
    setCurrentUserCanCreateApps(currUserCanCreateApps);
  }, [props.allowedAndDeniedOperationsDTO, currentOrgId, checkUserHasPermissionDefaultValue]);
  const loadApps = useCallback(async () => {
    return getAppsList(currentOrgId, 0, 9999);
  }, [getAppsList, currentOrgId]);
  useEffect(() => {
    if (props.currentUser) {
      loadApps().then(response => {
        setApps(Array.from(response.content));
      }).catch();
    }
  }, [props.currentUser, loadApps]);
  const onRowClick = async (record: any) => {
    const currentApp = record;
    if (currentApp.id) {
      await props.setCurrentApp(currentApp.id);
    }
    history.push({
      pathname: RoutePaths.APP_DETAILS(currentApp.id),
      state: {
        currentApp
      }
    });
  };
  const filteredApps = useMemo(() => apps.filter(app => app.name?.toLowerCase().includes(search.toLowerCase())), [apps, search]);
  const handleSearch = useDebounced((value: string) => {
    setSearch(value);
  });
  return <Content className="cw-app-content-container" data-sentry-element="Content" data-sentry-component="AppsComponent" data-sentry-source-file="index.tsx">
      <AppsPageHeader currentUserCanCreateApps={currentUserCanCreateApps} setSearch={handleSearch} data-sentry-element="AppsPageHeader" data-sentry-source-file="index.tsx" />
      <AppsTable apps={filteredApps} isLoadingApp={props.isLoadingApp} setCurrentApp={props.setCurrentApp} onRowClick={onRowClick} data-sentry-element="AppsTable" data-sentry-source-file="index.tsx" />
    </Content>;
};
const mapStateToProps = (state: AppState) => {
  const pageNumberState = state.apps.pagedApp?.number ? state.apps.pagedApp?.number + 1 : 1;
  return {
    appList: getAppList(state),
    pages: state.apps.pagedApp,
    pageNumber: pageNumberState,
    isLoadingApp: getCurrentAppLoadingStatus(state),
    getCurrentApp: getCurrentApp(state),
    currentOrgId: getCurrentOrgId(state),
    currentUser: getCurrentUser(state),
    allowedAndDeniedOperationsDTO: getAllowedAndDeniedOperationsDTO(state),
    rbacPermissionsCheck: (id: UUID, context: string, permission: string, scope: string, isDevConsoleCheck = false) => rbacPermissionsCheck(state, id, context, permission, scope, isDevConsoleCheck),
    currentUserHasPermission: (id: UUID, context: string, permission: string, scope: string, isDevConsoleCheck = false) => currentUserHasPermission(state, id, context, permission, scope, isDevConsoleCheck)
  };
};
const mapDispatchToProps = (dispatch: any) => {
  return {
    getAppsList: (orgId: UUID, pageNumber: number, pageSize: number) => dispatch(fetchApps(orgId, pageNumber, pageSize)),
    setCurrentApp: (appId: UUID) => dispatch(setCurrentApp(appId)),
    verifyRbacPermissions: (operationsListDTO: OperationListDTO) => dispatch(verifyRbacPermissions(operationsListDTO))
  };
};
export const Apps = withRouter(connect(mapStateToProps, mapDispatchToProps)(AppsComponent));